import { Col, Descriptions, Form, Result, Row, Spin } from 'antd';
import { useEffect, useState } from 'react';
import { CommonForm } from '../Shared/CommonForm';
import { FormFooter } from '../Shared/FormFooter';
import {
  FormContainer,
  FormItemInputNumberStyled,
  FormItemInputStyled,
  FormItemStyled,
  PageTitle,
  PrimaryButton,
} from '../Styled/GlobalStyles';
import { CloseCircleOutlined } from '@ant-design/icons';
import { logEvent } from '../../util/Analytics';
import { logUserTrack } from '../Shared/userTrack';

const CertifyGuarantee = () => {
  const [form] = Form.useForm();
  const [certifyGuarantee, setCertifyGuarantee] = useState<any>(null);
  const [certifyGuaranteeError, setCertifyGuaranteeError] = useState<any>(null);
  const [isCertifyGuaranteeLoading, setIsCertifyGuaranteeLoading] = useState<boolean>(false);
  const [isFormDisabled, setIsFormDisabled] = useState<boolean>(true);

  const container = Form.useWatch('containerNumber', form);
  const billOfLading = Form.useWatch('billOfLading', form);
  const guaranteeAmount = Form.useWatch('guaranteeAmount', form);
  const password = Form.useWatch('password', form);
  const [isContainerInputValid, setIsContainerInputValid] = useState<boolean>(true);

  useEffect(() => {
    document.title = 'Certify Guarantee';
  }, []);

  const onFormSubmit = (values: any) => {
    logEvent('certify_guarantee_submit');

    if (!values.containerNumber && !values.billOfLading && !values.guaranteeAmount && !values.password) {
      console.log('Form Submitted with invalid values!');
      return;
    }

    try {
      logUserTrack('click', 'submit_certify_guarantee_form', values);
    } catch (error) {
      console.log(error);
    }

    setIsCertifyGuaranteeLoading(true);
    fetch(`/api/certifyGuarantee`, {
      method: 'post',
      body: JSON.stringify(values),
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status === true) {
          setCertifyGuarantee(data);
        } else {
          setCertifyGuaranteeError(data);
        }
      })
      .finally(() => setIsCertifyGuaranteeLoading(false));
  };

  const userClickedClearForm = () => {
    logEvent('certify_guarantee_clear');
    resetForm();
  };

  const userClickedSearchAgain = () => {
    logEvent('certify_guarantee_search_again');
    resetForm();
  };

  const userClickedGoBack = () => {
    logEvent('certify_guarantee_certify_another');
    resetForm();
  };

  const resetForm = () => {
    form.resetFields();
    setCertifyGuaranteeError(null);
    setCertifyGuarantee(null);
  };

  useEffect(() => {
    if (container) {
      const containerRegex = /^[A-Za-z]{4}\s?\d{6}-?\d$/;
      let isContainerInputValid = containerRegex.test(container);
      setIsContainerInputValid(isContainerInputValid);
    } else {
      setIsContainerInputValid(true);
    }
    setIsFormDisabled(
      !(container && billOfLading && guaranteeAmount && password && guaranteeAmount > 0 && isContainerInputValid)
    );
  }, [container, billOfLading, guaranteeAmount, password]);

  return (
    <>
      <PageTitle>Certify A Guaranteed Payment</PageTitle>
      {!certifyGuarantee && !certifyGuaranteeError && (
        <CommonForm form={form} onFinish={onFormSubmit}>
          <Spin spinning={isCertifyGuaranteeLoading} size='large'>
            <FormContainer>
              <FormItemStyled name='containerNumber' label='Container'>
                <FormItemInputStyled
                  placeholder='Enter container'
                  size='large'
                  allowClear={true}
                  status={isContainerInputValid ? '' : 'error'}
                />
              </FormItemStyled>
              <FormItemStyled name='billOfLading' label='BOL'>
                <FormItemInputStyled placeholder='Enter BOL' size='large' allowClear={true} />
              </FormItemStyled>
              <FormItemStyled name='guaranteeAmount' label='Guarantee Amount'>
                <FormItemInputNumberStyled
                  placeholder='Amount'
                  size='large'
                  type='number'
                  min={0}
                  prefix='$'
                  precision={2}
                  controls={false}
                  style={{ width: 200 }}
                />
              </FormItemStyled>
              <FormItemStyled name='password' label='Password'>
                <FormItemInputStyled type='password' size='large' allowClear={true} />
              </FormItemStyled>
            </FormContainer>
            <FormFooter searchDisabled={isFormDisabled} resetForm={userClickedClearForm} submitText={'Submit'} />
          </Spin>
        </CommonForm>
      )}
      {certifyGuaranteeError && (
        <>
          <FormContainer>
            <Result
              status='error'
              title='Error'
              subTitle={certifyGuaranteeError.message}
              icon={<CloseCircleOutlined />}
              extra={[
                <PrimaryButton type='primary' key='console' onClick={userClickedSearchAgain}>
                  Search Again
                </PrimaryButton>,
              ]}
            ></Result>
          </FormContainer>
        </>
      )}

      {certifyGuarantee && (
        <>
          <FormContainer>
            <Result
              status='success'
              title='Certify Guarantee Successful'
              subTitle={
                <>
                  <Row justify={'center'}>
                    <Col span={12}>
                      <Descriptions layout='horizontal' bordered column={1}>
                        <Descriptions.Item label='Guarantee Amount'>
                          ${certifyGuarantee.guaranteedPayment.amount}
                        </Descriptions.Item>
                        <Descriptions.Item label='Container Number'>
                          {certifyGuarantee.containerDetails.containerNumber}
                        </Descriptions.Item>
                        <Descriptions.Item label='Bill Of Lading'>
                          {certifyGuarantee.containerDetails.billOfLading}
                        </Descriptions.Item>
                      </Descriptions>
                    </Col>
                  </Row>
                </>
              }
              extra={[
                <PrimaryButton type='primary' key='console' onClick={userClickedGoBack}>
                  Go Back
                </PrimaryButton>,
              ]}
            />
          </FormContainer>
        </>
      )}
    </>
  );
};

export default CertifyGuarantee;
